import { computed, ref } from 'vue'

import {
  useUpdateAccountNameMutation,
  type User,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'

export function useChangeName(user: User) {
  const toastStore = useToastStore()
  const webviewStore = useWebviewStore()

  const firstName = ref(user.firstName)
  const lastName = ref(user.lastName)

  const { mutate: updateAccountName, loading } = useUpdateAccountNameMutation()

  const isUnchanged = computed(
    () =>
      firstName.value.trim() === user.firstName.trim() &&
      lastName.value.trim() === user.lastName.trim()
  )

  const canSave = computed(() => !!firstName.value.trim() && !isUnchanged.value)

  const save = async () => {
    if (!canSave.value) return false

    const name = `${firstName.value} ${lastName.value}`

    updateAccountName(
      { firstName: firstName.value, lastName: lastName.value },
      {
        optimisticResponse: {
          __typename: 'Mutation',
          updateAccountName: {
            __typename: 'User',
            id: user.id,
            firstName: firstName.value,
            lastName: lastName.value,
            name,
          },
        },
        update() {
          if (!webviewStore.isActive) return

          webviewStore.postMessage({
            type: NativeActionsEnum.UserUpdated,
            data: {
              user: {
                ...user,
                firstName: firstName.value,
                lastName: lastName.value,
                name,
              },
            },
          })
        },
      }
    )

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Your name has been updated',
    })

    return true
  }

  return { firstName, lastName, loading, canSave, save }
}
